/**
 * Helper utilities for goal progress calculations
 * Extracted from goalCalculations.js to reduce file size and improve maintainability
 */

/**
 * Check if two goals are the same
 * @param {string} goal1 - First goal identifier
 * @param {string} goal2 - Second goal identifier
 * @returns {boolean} True if goals are the same
 */
export const isSameGoal = (goal1, goal2) => {
  if (goal1 === 'main' && goal2 === 'main') {
    return true;
  }

  if (goal1 === 'main' || goal2 === 'main') {
    return false;
  }

  return Number(goal1) === Number(goal2);
};

/**
 * Find the most recent subgoal from data points
 * @param {Array} dataPoints - Array of data points
 * @returns {string} The most recent goal identifier
 */
export const findMostRecentSubgoals = (dataPoints) => {
  if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
    return 'main';
  }

  const sortedDataPoints = [...dataPoints].sort((a, b) => {
    const aTime = a.currentTime ? new Date(a.currentTime).getTime() : 0;
    const bTime = b.currentTime ? new Date(b.currentTime).getTime() : 0;
    return bTime - aTime;
  });

  return sortedDataPoints[0].currentGoal || 'main';
};

/**
 * Get target information for a specific goal
 * @param {Object} goal - The goal object
 * @param {string} currentGoal - Current goal identifier
 * @param {Object} dataCollection - Data collection configuration
 * @returns {Object} Target and target duration
 */
export const getTargetInfo = (goal, currentGoal, dataCollection) => {
  let target;
  let targetDuration;

  if (currentGoal !== 'main' && !isNaN(currentGoal)) {
    const index = Number(currentGoal) - 1;
    const rawMeasurement =
      goal?.info?.HasSubgoal?.Subgoals?.[index]?.Measurement;

    const measurement =
      typeof rawMeasurement === 'string' && !isNaN(rawMeasurement)
        ? Number(rawMeasurement)
        : rawMeasurement;

    targetDuration =
      measurement ||
      Number.parseFloat(
        dataCollection.measurementType?.TargetDuration || 0
      );
    target =
      goal?.info?.HasSubgoal?.Subgoals?.[index]?.Target ||
      dataCollection.measurementType?.Target;
  } else {
    target = dataCollection.measurementType?.Target;
    targetDuration = Number.parseFloat(
      dataCollection.measurementType?.TargetDuration || 0
    );
  }

  return { target, targetDuration };
};

/**
 * Process values based on operation type
 * @param {Array} values - Array of values to process
 * @param {string} typeOfOperation - Type of operation (Average, Add, Take Highest, Take Lowest)
 * @returns {number} Processed value
 */
export const processValuesByOperation = (values, typeOfOperation) => {
  switch (typeOfOperation) {
    case 'Average':
      return values.length > 0
        ? values.reduce((a, b) => a + b, 0) / values.length
        : 0;
    case 'Add':
      return values.reduce((a, b) => a + b, 0);
    case 'Take Highest':
      return values.length > 0 ? Math.max(...values) : 0;
    case 'Take Lowest':
      return values.length > 0 ? Math.min(...values) : 0;
    default:
      return values;
  }
};

/**
 * Calculate progress value based on target type
 * @param {number} value - Current value
 * @param {string} target - Target type (At least, At most, etc.)
 * @param {number} targetDuration - Target duration value
 * @returns {number} Progress percentage
 */
export const calculateProgressValue = (value, target, targetDuration) => {
  if (target === 'At least' || target === 'Atleast') {
    return (value / targetDuration) * 100;
  } else if (target === 'At most' || target === 'Atmost') {
    if (value === 0) {
      return 1 - (1 - targetDuration) * 100;
    } else {
      return (1 - (value - targetDuration) / value) * 100;
    }
  }
  return 0;
};

/**
 * Sort data points by timestamp
 * @param {Array} dataPoints - Array of data points
 * @returns {Array} Sorted data points
 */
export const sortDataPointsByTime = (dataPoints) => {
  if (!Array.isArray(dataPoints)) return [];
  
  return [...dataPoints].sort((a, b) => {
    const aTime = a.currentTime ? new Date(a.currentTime).getTime() : 0;
    const bTime = b.currentTime ? new Date(b.currentTime).getTime() : 0;
    return bTime - aTime;
  });
};

/**
 * Sort goal data by timestamp
 * @param {Array} goalData - Array of goal data entries
 * @returns {Array} Sorted goal data
 */
export const sortGoalDataByTime = (goalData) => {
  return goalData.sort((a, b) => {
    const aTimestamp = a.createdAt?._seconds ? a.createdAt._seconds : 0;
    const bTimestamp = b.createdAt?._seconds ? b.createdAt._seconds : 0;

    if (
      aTimestamp === bTimestamp &&
      a.info?.data_points?.[0] &&
      b.info?.data_points?.[0]
    ) {
      const aLatestPoint = a.info.data_points[0];
      const bLatestPoint = b.info.data_points[0];

      const aCurrentTime = aLatestPoint?.currentTime
        ? new Date(aLatestPoint.currentTime).getTime()
        : 0;
      const bCurrentTime = bLatestPoint?.currentTime
        ? new Date(bLatestPoint.currentTime).getTime()
        : 0;

      return bCurrentTime - aCurrentTime;
    }

    return bTimestamp - aTimestamp;
  });
};

/**
 * Filter goal data by date
 * @param {Array} goalData - Array of goal data entries
 * @param {Date|null} specificDate - Optional specific date to filter by
 * @returns {Array} Filtered goal data
 */
export const filterGoalDataByDate = (goalData, specificDate = null) => {
  if (specificDate) {
    const specificDateString = specificDate.toDateString();
    return goalData.filter((entry) => {
      if (!entry.createdAt?._seconds) return false;
      const entryDate = new Date(entry.createdAt._seconds * 1000);
      return entryDate.toDateString() === specificDateString;
    });
  } else if (goalData.length > 0) {
    const mostRecentTimestamp = goalData[0].createdAt?._seconds;
    if (mostRecentTimestamp) {
      const mostRecentDate = new Date(
        mostRecentTimestamp * 1000
      ).toDateString();
      return goalData.filter((entry) => {
        if (!entry.createdAt?._seconds) return false;
        const entryDate = new Date(entry.createdAt._seconds * 1000);
        return entryDate.toDateString() === mostRecentDate;
      });
    }
  }
  
  return goalData;
};

/**
 * Extract values from data points for a specific goal
 * @param {Array} dataPoints - Array of data points
 * @param {string} mostRecentGoal - Most recent goal identifier
 * @returns {Array} Array of numeric values
 */
export const extractValuesFromDataPoints = (dataPoints, mostRecentGoal) => {
  const values = [];
  
  if (Array.isArray(dataPoints)) {
    const sortedDataPoints = sortDataPointsByTime(dataPoints);
    
    sortedDataPoints
      .filter((point) => isSameGoal(point.currentGoal, mostRecentGoal))
      .forEach((point) => {
        values.push(Number.parseFloat(point.goal_value || 0));
      });
  }
  
  return values;
}; 